import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "preselection-rule" }
const _hoisted_2 = { class: "criteria-name" }
const _hoisted_3 = { class: "criteria-data" }
const _hoisted_4 = { class: "preselection-data" }
const _hoisted_5 = {
  key: 0,
  class: "connection-text"
}
const _hoisted_6 = {
  key: 1,
  class: "attribute-group"
}
const _hoisted_7 = { class: "criteria-manipulation" }
const _hoisted_8 = { class: "criteria-operator" }
const _hoisted_9 = {
  key: 0,
  class: "criteria-value"
}
const _hoisted_10 = { class: "criteria-meta-info" }
const _hoisted_11 = { class: "query-builder-rule-language-selection" }
const _hoisted_12 = { class: "query-builder-rule-actions" }
const _hoisted_13 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Close" }, null, -1)

import MSelect from "@/BasicComponentLibrary/MSelect.vue";
import { getTranslatedSelectionOpsList, requiresOperatorLanguageSelection, requiresOperatorValueInput } from "@AdvancedSearch/Util/opsLookUp";
import ERPAttributeSelectionModal from "@common/components/MultiLevelNavigation/ERPAttributeSelectionModal.vue";
import GlobalAttributeSelectionModal from "@common/components/MultiLevelNavigation/GlobalAttributeSelectionModal.vue";
import MediaAssetValueSelectionModal from "@common/components/MultiLevelNavigation/MediaAssetValueSelectionModal.vue";
import TextTableAttributeSelectionModal from "@common/components/MultiLevelNavigation/TextTableAttributeSelectionModal.vue";
import PathPresenter from "@common/components/PathPresenter.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import {
  AllowedOps,
  DataUITypes,
  OpsSelectionModel,
  QueryAdditionalOperatorSupport,
  QueryBuilderRule,
  SearchTypes
} from "@medapro-types/AdvancedSearch/components/QueryBuilder";
import { AttributeDataType } from "@medapro-types/common/common-enum";
import { Branch, MultiLevelNavigationConfirmModel, MultiLevelNavigationSelectionModel } from "@medapro-types/common/components/MultiLevelNavigation";
import attributesApi from "@services/attributes.js";
import erpAttributesApi from "@services/erpAttributes.js";
import globalAttributesApi from "@services/globalAttributes.js";
import mediaAssets from "@services/mediaAssets.js";
import { BaseIds } from "@util/enums/base-ids";
import _ from "lodash";
import { computed, inject, nextTick, onMounted, reactive, Ref, unref, watch } from "vue";
import { useI18n } from "vue-i18n";
import BoolRule from "../QueryRules/BoolRule.vue";
import DateRule from "../QueryRules/DateRule.vue";
import NumberRule from "../QueryRules/NumberRule.vue";
import StringRule from "../QueryRules/StringRule.vue";

//#region General
type ViewModel = {
  localModel: QueryBuilderRule;
  ops: OpsSelectionModel[];
  localDataValueType: DataUITypes | null;
  preselectionInfo: Record<string, any> | null | undefined;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'PreselectionRule',
  props: {
    value: {},
    languageRequired: { type: [Boolean, null] },
    additionalOperatorSupport: {}
  },
  emits: ["input", "deleteRule", "refreshBackground"],
  setup(__props: any, { emit: emits }) {

const props = __props;




const { t: $t } = useI18n();
const isBusy = inject<Ref<boolean>>("advancedSearchIsBusy");
//#endregion

//#region Store
const { primaryLanguageCode, localizedLanguages: localizedLanguages } = useContentLanguages();
//#endregion

//#region Languages
const languageSelection = computed(() => {
  const result = [
    { id: "", name: `<${$t("all")}>` },
    ...(unref(localizedLanguages)?.map((x) => ({
      id: x.languageCode,
      name: x.localizedName
    })) ?? [])
  ];

  return result;
});

//#endregion

//#region Busy
const setBusyState = (busyValue: boolean) => {
  if (isBusy) {
    isBusy.value = busyValue;
  }
};
//#endregion

//#region Modal
const getAttributeTypeComponent = () => {
  switch (vm.localModel.searchType) {
    case "tableAttribute":
    case "textAttribute":
      return TextTableAttributeSelectionModal;
    case "globalAttribute":
      return GlobalAttributeSelectionModal;
    case "erpAttribute":
      return ERPAttributeSelectionModal;
    case "mediaAssetValue":
      return MediaAssetValueSelectionModal;
  }
};

const allEntry: MultiLevelNavigationSelectionModel = {
  id: null,
  name: `<${$t("all")}>`,
  type: "all",
  dataType: AttributeDataType.AttributeDataTypeAlphanumerical
};

const loadPreselectionInfo = async (searchType: SearchTypes) => {
  setBusyState(true);
  if (vm.localModel.attributeId === null) {
    vm.preselectionInfo = allEntry;
  } else {
    if (vm.localModel.attributeId && vm.localModel.attributeId != "unselected" && !vm.preselectionInfo) {
      let fetchedList: any[] = [];
      let baseNodeIndex: number;
      let preselectionInfo: Record<string, any> | null | undefined = {};
      switch (searchType) {
        case "tableAttribute":
        case "textAttribute":
          fetchedList = await attributesApi.getDetails(primaryLanguageCode.value, [vm.localModel.attributeId]);

          preselectionInfo = _.first(fetchedList);
          if (preselectionInfo && preselectionInfo.paths) {
            baseNodeIndex = _.findIndex(preselectionInfo.paths, (x: any) => x.id == BaseIds.BaseAttributeGroup);
            if (baseNodeIndex != -1) preselectionInfo.paths.splice(baseNodeIndex, 1);
          }
          break;
        case "erpAttribute":
          fetchedList = await erpAttributesApi.getDetails(primaryLanguageCode.value, [vm.localModel.attributeId]);
          preselectionInfo = _.first(fetchedList);
          break;
        case "globalAttribute":
          fetchedList = await globalAttributesApi.getDetails(primaryLanguageCode.value, [vm.localModel.attributeId]);
          preselectionInfo = _.first(fetchedList);
          break;
        case "mediaAssetValue":
          fetchedList = await mediaAssets.getDetails(primaryLanguageCode.value, [vm.localModel.attributeId]);
          preselectionInfo = _.first(fetchedList);
          if (preselectionInfo) preselectionInfo.dataType = AttributeDataType.AttributeDataTypeAlphanumerical;
          break;
      }
      updatePreselectionInfoPaths(preselectionInfo?.paths);
      vm.preselectionInfo = preselectionInfo;
    } else vm.localModel.attributeId = "unselected";
  }
  setBusyState(false);
};

const onSelection = (event: MultiLevelNavigationConfirmModel) => {
  if (event.selection) {
    vm.localModel.attributeId = event.selection.id;
    vm.preselectionInfo = event.selection;
    setLocalDataValueType();
    if (vm.localDataValueType == "date") {
      vm.localModel.value = "unselected";
    }
  }
  if (event.paths) {
    const paths = _.cloneDeep(event.paths);
    updatePreselectionInfoPaths(paths);
    if (vm.preselectionInfo) Object.assign(vm.preselectionInfo, { paths });
  }
};

const updatePreselectionInfoPaths = (paths: Branch[]) => {
  const homeEntry: Branch = {
    id: "",
    name: null,
    fallbackName: null,
    depth: 0,
    icon: "Home",
    parentId: null
  };

  if (paths) {
    paths.forEach((x) => delete x.icon);
    if (paths.length == 0) {
      if (vm.localModel.searchType === "textAttribute") {
        homeEntry.name = $t("text.attributes");
      } else if (vm.localModel.searchType === "tableAttribute") {
        homeEntry.name = $t("table.attributes");
      }
    }
    paths.unshift(homeEntry);
  }
};

const onAttributeCancel = () => {
  if (vm.localModel.attributeId == "unselected") emits("deleteRule");
};

const getValueSelectionComponentByType = () => {
  switch (vm.localDataValueType) {
    case "numeric":
      return NumberRule;
    case "logical":
      return BoolRule;
    case "date":
      return DateRule;
    case "interval":
      return NumberRule;

    default:
      return StringRule;
  }
};
//#endregion

//#region ViewModel
const vm = reactive<ViewModel>({
  localModel: props.value,
  ops: [],
  localDataValueType: null,
  preselectionInfo: null
});

const modals = reactive({ isOpen: false });
//#endregion

//#region Controle
const showAttributeGroupsPath = computed(() => {
  return vm.localModel.searchType != "globalAttribute" && vm.localModel.searchType != "mediaAssetValue" && vm.localModel.attributeId !== null;
});

const languageSelectionRequired = computed(() => {
  return props.languageRequired && requiresOperatorLanguageSelection(vm.localModel.operator);
});

const valueInputRequired = computed(() => {
  return requiresOperatorValueInput(vm.localModel.operator);
});
//#endregion

//#region LocalDataValueType

const setLocalDataValueType = () => {
  if (vm.preselectionInfo) {
    switch (vm.preselectionInfo.dataType) {
      case AttributeDataType.AttributeDataTypeAlphanumerical:
        vm.localDataValueType = "alphanumeric";
        break;
      case AttributeDataType.AttributeDataTypeDate:
        vm.localDataValueType = "date";
        break;
      case AttributeDataType.AttributeDataTypeInterval:
        vm.localDataValueType = "interval";
        break;
      case AttributeDataType.AttributeDataTypeLogical:
        vm.localDataValueType = "logical";
        break;
      case AttributeDataType.AttributeDataTypeNumerical:
        vm.localDataValueType = "numeric";
        break;
    }
    if (vm.localDataValueType && !vm.localModel.operator) {
      const firstOperator = ((_.first(vm.ops)?.id ?? vm.localDataValueType !== "interval") ? "equals" : "includes") satisfies AllowedOps;
      vm.localModel.operator = firstOperator;
    }
  }
};

watch(
  [() => vm.localDataValueType, () => vm.localModel.value],
  ([type]) => {
    if (type) {
      if (vm.localDataValueType === "date" && vm.localModel.value === "")
        vm.ops = getTranslatedSelectionOpsList(type, props.additionalOperatorSupport).filter((o) => o.id === "equals" || o.id === "equalsNot");
      else vm.ops = getTranslatedSelectionOpsList(type, props.additionalOperatorSupport);
    }
  },
  { immediate: true }
);
//#endregion

//#region LifeCycle
onMounted(async () => {
  await loadPreselectionInfo(vm.localModel.searchType);
  setLocalDataValueType();
  if (vm.localModel.dataLanguage === undefined) {
    vm.localModel.dataLanguage = "";
  }
  nextTick(() => {
    if (vm.localModel.attributeId == "unselected" || vm.localModel.value == "unselected") {
      modals.isOpen = true;
    }
  });
});
//#endregion

return (_ctx: any,_cache: any) => {
  const _component_b_btn = _resolveComponent("b-btn")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, _toDisplayString(_unref($t)("AdvancedSearch.SearchType." + vm.localModel.searchType)), 1),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_component_b_btn, {
          variant: "link",
          class: "attribute-value",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (modals.isOpen = true))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(vm.preselectionInfo ? vm.preselectionInfo?.name : ""), 1)
          ]),
          _: 1
        }),
        (showAttributeGroupsPath.value && vm.preselectionInfo?.paths && vm.preselectionInfo.paths.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref($t)("in")), 1))
          : _createCommentVNode("", true),
        (showAttributeGroupsPath.value && vm.preselectionInfo?.paths)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(PathPresenter, {
                modelValue: vm.preselectionInfo.paths,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((vm.preselectionInfo.paths) = $event))
              }, null, 8, ["modelValue"])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _createVNode(MSelect, {
            modelValue: vm.localModel.operator,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((vm.localModel.operator) = $event)),
            items: vm.ops,
            textField: "name",
            valueField: "id",
            "data-cy": "rule-operator"
          }, null, 8, ["modelValue", "items"])
        ]),
        (valueInputRequired.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              (_openBlock(), _createBlock(_resolveDynamicComponent(getValueSelectionComponentByType()), {
                "can-be-none": "",
                modelValue: vm.localModel.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((vm.localModel.value) = $event)),
                onDeleteRule: _cache[4] || (_cache[4] = ($event: any) => (emits('deleteRule')))
              }, null, 40, ["modelValue"]))
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", _hoisted_10, [
      _createElementVNode("div", _hoisted_11, [
        (languageSelectionRequired.value)
          ? (_openBlock(), _createBlock(MSelect, {
              key: 0,
              modelValue: vm.localModel.dataLanguage,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((vm.localModel.dataLanguage) = $event)),
              items: languageSelection.value,
              textField: "name",
              valueField: "id",
              "data-cy": "rule-language"
            }, null, 8, ["modelValue", "items"]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createVNode(_component_b_btn, {
          class: "btn-icon mono",
          onClick: _cache[6] || (_cache[6] = ($event: any) => (emits('deleteRule'))),
          "data-cy": "rule-clear"
        }, {
          default: _withCtx(() => [
            _hoisted_13
          ]),
          _: 1
        })
      ])
    ]),
    (_openBlock(), _createBlock(_resolveDynamicComponent(getAttributeTypeComponent()), {
      modelValue: modals.isOpen,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((modals.isOpen) = $event)),
      "attribute-type": vm.localModel.searchType == 'textAttribute' ? 'text' : 'table',
      "selected-entry-id": vm.preselectionInfo?.id,
      "selected-path": vm.preselectionInfo?.paths,
      onConfirm: onSelection,
      onHide: onAttributeCancel
    }, null, 40, ["modelValue", "attribute-type", "selected-entry-id", "selected-path"]))
  ]))
}
}

})